import { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardActions,
  Grid,
  Tabs,
  Tab,
  LinearProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Backdrop,
  Skeleton,
} from '@mui/material'
import {
  Add,
  Edit,
  Visibility,
  Assignment,
  Folder,
  Schedule,
  ArrowBack,
  ArrowForward,
  SwapHoriz,
  Upload,
  Search,
  Description,
  Download,
  Delete,
  Timeline,
  CalendarToday,
  VideoCall,
  People,
  OpenInNew,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { 
  useGetCasesQuery, 
  useCreateCaseMutation, 
  useUpdateCaseMutation,
  useDeleteCaseMutation,
  useAssignCaseMutation,
  useGetCaseQuery,
  useMoveToNextRoundMutation,
  useMoveToRoundMutation,
  type Case,
  type CaseCreate,
} from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import {
  useGetCaseRoundsQuery,
  useUpdateRoundMutation,
  type Round,
  RoundType,
  RoundStatus,
} from '../store/api/roundsApi'
import { hasPermission } from '../utils/permissions'
import { 
  useGetDocumentsQuery, 
  useUploadDocumentMutation, 
  useDeleteDocumentMutation,
  useLazyDownloadDocumentQuery,
  DocumentType 
} from '../store/api/documentsApi'
import { API_BASE_URL } from '../config/api'
import { formatCaseDisplay } from '../utils/caseDisplay'
import {
  useGetCourtroomByRoundQuery,
  useJoinCourtroomMutation,
  useEndCourtroomMutation,
  CourtroomStatus,
} from '../store/api/courtroomsApi'

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Bản nháp' },
  { value: 'assigned', label: 'Đã phân bổ' },
  { value: 'in_progress', label: 'Đang thực hiện' },
  { value: 'submitted', label: 'Đã nộp bài' },
  { value: 'scored', label: 'Đã chấm điểm' },
]

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  draft: 'default',
  assigned: 'info',
  in_progress: 'primary',
  submitted: 'warning',
  scored: 'success',
}

const CASE_TYPE_NAMES: Record<string, string> = {
  'dân_sự': 'Dân sự',
  'hình_sự': 'Hình sự',
  'hành_chính': 'Hành chính',
  'lao_động': 'Lao động',
  'thương_mại': 'Thương mại',
  'hôn_nhân_gia_đình': 'Hôn nhân & Gia đình',
  'khác': 'Khác',
}

const getCaseTypeName = (caseType?: string): string => {
  if (!caseType) return '-'
  return CASE_TYPE_NAMES[caseType] || caseType
}

export default function CasesPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: cases = [], isLoading, refetch } = useGetCasesQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const [createCase, { isLoading: isCreating }] = useCreateCaseMutation()
  const [updateCase, { isLoading: isUpdating }] = useUpdateCaseMutation()
  const [deleteCase] = useDeleteCaseMutation()

  const [openDialog, setOpenDialog] = useState(false)
  const [viewDialog, setViewDialog] = useState(false)
  const [editingCase, setEditingCase] = useState<Case | null>(null)
  const [viewingCase, setViewingCase] = useState<Case | null>(null)
  const [formData, setFormData] = useState<CaseCreate>({
    title: '',
    parties_requests: '',
    description: '',
    case_number: '',
    case_type: '',
    court: '',
    plaintiff: '',
    defendant: '',
    incident_date: '',
    filing_date: '',
    dispute_value: '',
    legal_area: '',
    summary: '',
    deadline: '',
  })
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Check permissions
  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  const canViewAllCases = currentUser && hasPermission(currentUser.role, 'VIEW_CASES')
  const canViewOwnCase = currentUser && hasPermission(currentUser.role, 'VIEW_OWN_CASE')

  // Get user's team for filtering
  const userTeam = teams.find(t => 
    t.members.includes(currentUser?.id || '') || 
    t.coach_id === currentUser?.id
  )

  // Filter cases based on permissions
  const displayCases = canViewAllCases
    ? cases
    : canViewOwnCase && userTeam
      ? cases.filter(c => c.assigned_team_ids && c.assigned_team_ids.includes(userTeam.id))
      : []

  const handleOpenDialog = (caseItem?: Case) => {
    if (caseItem) {
      setEditingCase(caseItem)
      setFormData({
        title: caseItem.title,
        parties_requests: caseItem.parties_requests || '',
        description: caseItem.description || '',
        case_number: caseItem.case_number || '',
        case_type: caseItem.case_type || '',
        court: caseItem.court || '',
        plaintiff: caseItem.plaintiff || '',
        defendant: caseItem.defendant || '',
        incident_date: caseItem.incident_date ? new Date(caseItem.incident_date).toISOString().slice(0, 16) : '',
        filing_date: caseItem.filing_date ? new Date(caseItem.filing_date).toISOString().slice(0, 16) : '',
        dispute_value: caseItem.dispute_value || '',
        legal_area: caseItem.legal_area || '',
        summary: caseItem.summary || '',
        deadline: caseItem.deadline ? new Date(caseItem.deadline).toISOString().slice(0, 16) : '',
      })
    } else {
      setEditingCase(null)
      setFormData({
        title: '',
        parties_requests: '',
        description: '',
        case_number: '',
        case_type: '',
        court: '',
        plaintiff: '',
        defendant: '',
        incident_date: '',
        filing_date: '',
        dispute_value: '',
        legal_area: '',
        summary: '',
        deadline: '',
      })
    }
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingCase(null)
    setFormData({
      title: '',
      description: '',
      case_number: '',
      case_type: '',
      court: '',
      plaintiff: '',
      defendant: '',
      incident_date: '',
      filing_date: '',
      dispute_value: '',
      legal_area: '',
      summary: '',
      deadline: '',
    })
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleViewCase = (caseItem: Case) => {
    setViewingCase(caseItem)
    setViewDialog(true)
  }

  const handleCloseViewDialog = () => {
    setViewDialog(false)
    setViewingCase(null)
  }

  // Delete case handler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [caseToDelete, setCaseToDelete] = useState<Case | null>(null)

  const handleDeleteClick = (e: React.MouseEvent, caseItem: Case) => {
    e.stopPropagation()
    setCaseToDelete(caseItem)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!caseToDelete) return

    try {
      await deleteCase(caseToDelete.id).unwrap()
      setSuccessMessage('Xóa cuộc thi thành công!')
      refetch()
    } catch (error: any) {
      setErrorMessage(`Xóa cuộc thi thất bại: ${error.data?.detail || error.message}`)
    } finally {
      setDeleteConfirmOpen(false)
      setCaseToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setCaseToDelete(null)
  }

  const handleSubmit = async () => {
    setErrorMessage(null)
    setSuccessMessage(null)

    if (!formData.title.trim()) {
      setErrorMessage('Tiêu đề cuộc thi là bắt buộc')
      return
    }

    try {
      // Prepare data with proper datetime format
      const submitData: CaseCreate = {
        ...formData,
        // Convert datetime-local format to ISO string if present
        incident_date: formData.incident_date ? new Date(formData.incident_date).toISOString() : undefined,
        filing_date: formData.filing_date ? new Date(formData.filing_date).toISOString() : undefined,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : undefined,
      }

      if (editingCase) {
        await updateCase({ id: editingCase.id, ...submitData }).unwrap()
        setSuccessMessage('Cập nhật cuộc thi thành công!')
      } else {
        await createCase(submitData).unwrap()
        setSuccessMessage('Tạo cuộc thi thành công!')
      }
      handleCloseDialog()
      refetch()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra')
    }
  }


  // Get assigned team names
  const getAssignedTeamNames = (teamIds?: string[]) => {
    if (!teamIds || teamIds.length === 0) return '-'
    const teamNames = teamIds
      .map(teamId => {
        const team = teams.find(t => t.id === teamId)
        return team ? team.name : null
      })
      .filter(Boolean)
    return teamNames.length > 0 ? teamNames.join(', ') : '-'
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          {canAssignCases ? 'Quản lý Cuộc thi' : 'Cuộc thi của tôi'}
        </Typography>
        {canAssignCases && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
            }}
          >
            Tạo cuộc thi mới
          </Button>
        )}
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : displayCases.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Folder sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Chưa có cuộc thi nào
          </Typography>
          {canAssignCases && (
            <Typography variant="body2" color="text.secondary">
              Hãy tạo cuộc thi mới để bắt đầu
            </Typography>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Tiêu đề cuộc thi</strong></TableCell>
                <TableCell><strong>Loại cuộc thi</strong></TableCell>
                <TableCell><strong>Số đội được phân</strong></TableCell>
                <TableCell><strong>Deadline</strong></TableCell>
                <TableCell><strong>Trạng thái</strong></TableCell>
                <TableCell align="right"><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayCases.map((caseItem) => (
                <TableRow 
                  key={caseItem.id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: 'action.hover',
                      cursor: 'pointer'
                    } 
                  }}
                  onClick={() => handleViewCase(caseItem)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Folder fontSize="small" color="primary" />
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {formatCaseDisplay(caseItem)}
                        </Typography>
                        {caseItem.parties_requests && (
                          <Tooltip title={caseItem.parties_requests}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                display: 'block',
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {caseItem.parties_requests.length > 80 
                                ? `${caseItem.parties_requests.substring(0, 80)}...` 
                                : caseItem.parties_requests}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {caseItem.case_type ? (
                      <Chip 
                        label={getCaseTypeName(caseItem.case_type)} 
                        size="small" 
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {caseItem.assigned_team_ids && caseItem.assigned_team_ids.length > 0 ? (
                      <Tooltip title={getAssignedTeamNames(caseItem.assigned_team_ids)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Assignment fontSize="small" color="action" />
                          <Typography variant="body2">
                            {caseItem.assigned_team_ids.length} đội
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Chưa phân bổ</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {caseItem.deadline ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule fontSize="small" color="action" />
                        <Typography variant="body2">
                          {new Date(caseItem.deadline).toLocaleDateString('vi-VN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_OPTIONS.find(s => s.value === caseItem.status)?.label || caseItem.status}
                      color={STATUS_COLORS[caseItem.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleViewCase(caseItem)
                        }}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'white',
                          }
                        }}
                      >
                        Xem chi tiết
                      </Button>
                      {canViewAllCases && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, caseItem)}
                          color="error"
                          title="Xóa cuộc thi"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCase ? 'Chỉnh sửa cuộc thi' : 'Tạo cuộc thi mới'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, maxHeight: '70vh', overflowY: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Thông tin cơ bản</Typography>
            <TextField
              label="Tiêu đề cuộc thi"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Số vụ án"
                value={formData.case_number}
                onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Loại cuộc thi</InputLabel>
                <Select
                  value={formData.case_type}
                  onChange={(e) => setFormData({ ...formData, case_type: e.target.value })}
                  label="Loại cuộc thi"
                >
                  <MenuItem value="dân_sự">Dân sự</MenuItem>
                  <MenuItem value="hình_sự">Hình sự</MenuItem>
                  <MenuItem value="hành_chính">Hành chính</MenuItem>
                  <MenuItem value="lao_động">Lao động</MenuItem>
                  <MenuItem value="thương_mại">Thương mại</MenuItem>
                  <MenuItem value="hôn_nhân_gia_đình">Hôn nhân & Gia đình</MenuItem>
                  <MenuItem value="khác">Khác</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Tòa án xử lý"
              value={formData.court}
              onChange={(e) => setFormData({ ...formData, court: e.target.value })}
              fullWidth
            />

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Các bên liên quan</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Nguyên đơn"
                value={formData.plaintiff}
                onChange={(e) => setFormData({ ...formData, plaintiff: e.target.value })}
                fullWidth
              />
              <TextField
                label="Bị đơn"
                value={formData.defendant}
                onChange={(e) => setFormData({ ...formData, defendant: e.target.value })}
                fullWidth
              />
            </Box>

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Thông tin thời gian</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Ngày xảy ra sự kiện"
                type="datetime-local"
                value={formData.incident_date}
                onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Ngày khởi kiện"
                type="datetime-local"
                value={formData.filing_date}
                onChange={(e) => setFormData({ ...formData, filing_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Thông tin khác</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Giá trị tranh chấp"
                value={formData.dispute_value}
                onChange={(e) => setFormData({ ...formData, dispute_value: e.target.value })}
                placeholder="Ví dụ: 500.000.000 VNĐ"
                fullWidth
              />
              <TextField
                label="Lĩnh vực pháp luật"
                value={formData.legal_area}
                onChange={(e) => setFormData({ ...formData, legal_area: e.target.value })}
                placeholder="Ví dụ: Hợp đồng, Bất động sản, v.v."
                fullWidth
              />
            </Box>
            <TextField
              label="Tóm tắt cuộc thi"
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Yêu cầu các bên"
              value={formData.parties_requests}
              onChange={(e) => setFormData({ ...formData, parties_requests: e.target.value })}
              multiline
              rows={4}
              placeholder="Nhập yêu cầu của nguyên đơn và bị đơn..."
              fullWidth
              helperText="Mô tả chi tiết sẽ được quản lý qua upload tài liệu"
            />

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Deadline</Typography>
            <TextField
              label="Deadline nộp bài"
              type="datetime-local"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Hủy</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isCreating || isUpdating}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
            }}
          >
            {isCreating || isUpdating ? <CircularProgress size={20} /> : (editingCase ? 'Cập nhật' : 'Tạo')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Case Dialog */}
      {viewingCase && (
        <CaseDetailDialog
          open={viewDialog}
          onClose={handleCloseViewDialog}
          caseItem={viewingCase}
          teams={teams}
          onCaseUpdated={refetch}
        />
      )}

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc muốn xóa cuộc thi "{caseToDelete?.title}"?
            Hành động này sẽ xóa tất cả các vòng thi liên quan và không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Hủy</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Case Detail Dialog Component
function CaseDetailDialog({ 
  open, 
  onClose, 
  caseItem, 
  teams,
  onCaseUpdated
}: { 
  open: boolean
  onClose: () => void
  caseItem: Case
  teams: any[]
  onCaseUpdated?: () => void
}) {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const [tabValue, setTabValue] = useState(0)
  const canViewAllCases = currentUser && hasPermission(currentUser.role, 'VIEW_CASES')
  const canScoreCases = currentUser && hasPermission(currentUser.role, 'SCORE_CASES')
  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  
  // Refetch case data
  const { data: currentCaseData, refetch: refetchCase } = useGetCaseQuery(caseItem.id, { skip: !open })
  const [assignCase, { isLoading: isAssigning }] = useAssignCaseMutation()
  
  // Assignment dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(caseItem.assigned_team_ids || [])
  const [assignDeadline, setAssignDeadline] = useState<string>(
    caseItem.deadline ? new Date(caseItem.deadline).toISOString().slice(0, 16) : ''
  )
  const [assignSuccessMessage, setAssignSuccessMessage] = useState<string | null>(null)
  const [assignErrorMessage, setAssignErrorMessage] = useState<string | null>(null)
  
  // Documents
  const { data: documents = [], isLoading: isLoadingDocs } = useGetDocumentsQuery(
    { case_id: caseItem.id },
    { skip: !open || tabValue !== 1 }
  )
  const [uploadDocument, { isLoading: isUploading }] = useUploadDocumentMutation()
  const [deleteDocument] = useDeleteDocumentMutation()
  const [downloadDocument] = useLazyDownloadDocumentQuery()
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    document_type: DocumentType.CASE_DOCUMENT,
    file: null as File | null,
  })
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title) {
      alert('Vui lòng điền đầy đủ thông tin')
      return
    }

    const formData = new FormData()
    formData.append('file', uploadForm.file)
    formData.append('title', uploadForm.title)
    if (uploadForm.description) formData.append('description', uploadForm.description)
    formData.append('document_type', uploadForm.document_type)
    formData.append('case_id', caseItem.id)

    try {
      await uploadDocument(formData).unwrap()
      setUploadDialogOpen(false)
      setUploadForm({ title: '', description: '', document_type: DocumentType.CASE_DOCUMENT, file: null })
      alert('Upload thành công!')
    } catch (error: any) {
      alert(`Upload thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const handleDownload = async (docId: string, fileName: string) => {
    try {
      const result = await downloadDocument(docId).unwrap()
      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error: any) {
      alert(`Download thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const handleDelete = async (docId: string) => {
    if (!confirm('Bạn có chắc muốn xóa tài liệu này?')) return
    try {
      await deleteDocument(docId).unwrap()
      alert('Xóa thành công!')
    } catch (error: any) {
      alert(`Xóa thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const caseDocuments = documents.filter(
    (doc) => doc.document_type === DocumentType.CASE_DOCUMENT || doc.document_type === DocumentType.REFERENCE
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Folder />
          <Typography variant="h6">{formatCaseDisplay(caseItem)}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {assignSuccessMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAssignSuccessMessage(null)}>
            {assignSuccessMessage}
          </Alert>
        )}
        {assignErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssignErrorMessage(null)}>
            {assignErrorMessage}
          </Alert>
        )}
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab label="Thông tin" />
          {canViewAllCases && <Tab label="Vòng thi" />}
          {canViewAllCases && <Tab label="Tài liệu cuộc thi" />}
          {/* RAG chỉ dùng cho AI Assistive trong ScoringPage, không cần tab riêng ở đây */}
        </Tabs>

        {/* Tab 1: Thông tin */}
        {tabValue === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2, maxHeight: '70vh', overflowY: 'auto' }}>
          <Typography variant="h6" gutterBottom>Thông tin cơ bản</Typography>
          {caseItem.case_number && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Số vụ án
              </Typography>
              <Typography variant="body1">{caseItem.case_number}</Typography>
            </Box>
          )}
          {caseItem.case_type && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Loại cuộc thi
              </Typography>
              <Typography variant="body1">{getCaseTypeName(caseItem.case_type)}</Typography>
            </Box>
          )}
          {caseItem.court && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Tòa án xử lý
              </Typography>
              <Typography variant="body1">{caseItem.court}</Typography>
            </Box>
          )}

          <Typography variant="h6" sx={{ mt: 1 }} gutterBottom>Các bên liên quan</Typography>
          {caseItem.plaintiff && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Nguyên đơn
              </Typography>
              <Typography variant="body1">{caseItem.plaintiff}</Typography>
            </Box>
          )}
          {caseItem.defendant && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Bị đơn
              </Typography>
              <Typography variant="body1">{caseItem.defendant}</Typography>
            </Box>
          )}

          <Typography variant="h6" sx={{ mt: 1 }} gutterBottom>Thông tin thời gian</Typography>
          {caseItem.incident_date && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Ngày xảy ra sự kiện
              </Typography>
              <Typography variant="body1">
                {new Date(caseItem.incident_date).toLocaleString('vi-VN')}
              </Typography>
            </Box>
          )}
          {caseItem.filing_date && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Ngày khởi kiện
              </Typography>
              <Typography variant="body1">
                {new Date(caseItem.filing_date).toLocaleString('vi-VN')}
              </Typography>
            </Box>
          )}

          <Typography variant="h6" sx={{ mt: 1 }} gutterBottom>Thông tin khác</Typography>
          {caseItem.dispute_value && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Giá trị tranh chấp
              </Typography>
              <Typography variant="body1">{caseItem.dispute_value}</Typography>
            </Box>
          )}
          {caseItem.legal_area && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Lĩnh vực pháp luật
              </Typography>
              <Typography variant="body1">{caseItem.legal_area}</Typography>
            </Box>
          )}
          {caseItem.summary && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Tóm tắt cuộc thi
              </Typography>
              <Typography variant="body1">{caseItem.summary}</Typography>
            </Box>
          )}
          {caseItem.parties_requests && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Yêu cầu các bên
              </Typography>
              <Typography variant="body1">{caseItem.parties_requests}</Typography>
            </Box>
          )}
          {caseItem.description && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Mô tả chi tiết (Tài liệu)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Mô tả chi tiết sẽ được quản lý qua upload tài liệu của cuộc thi
              </Typography>
            </Box>
          )}

          <Typography variant="h6" sx={{ mt: 1 }} gutterBottom>Trạng thái và phân bổ</Typography>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Trạng thái
            </Typography>
            <Chip
              label={STATUS_OPTIONS.find(s => s.value === caseItem.status)?.label || caseItem.status}
              color={STATUS_COLORS[caseItem.status] || 'default'}
            />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Đội được phân bổ
              </Typography>
              {canAssignCases && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Assignment />}
                  onClick={() => {
                    setSelectedTeamIds(currentCaseData?.assigned_team_ids || caseItem.assigned_team_ids || [])
                    setAssignDeadline(
                      currentCaseData?.deadline 
                        ? new Date(currentCaseData.deadline).toISOString().slice(0, 16) 
                        : caseItem.deadline 
                          ? new Date(caseItem.deadline).toISOString().slice(0, 16) 
                          : ''
                    )
                    setAssignDialogOpen(true)
                  }}
                >
                  {caseItem.assigned_team_ids && caseItem.assigned_team_ids.length > 0 ? 'Chỉnh sửa phân bổ' : 'Phân bổ đội thi'}
                </Button>
              )}
            </Box>
            {(currentCaseData?.assigned_team_ids || caseItem.assigned_team_ids) && 
             (currentCaseData?.assigned_team_ids || caseItem.assigned_team_ids)!.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(currentCaseData?.assigned_team_ids || caseItem.assigned_team_ids)!.map((teamId) => {
                  const team = teams.find(t => t.id === teamId)
                  return team ? (
                    <Box key={teamId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={`${team.name} - ${team.university}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Box>
                  ) : null
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Chưa có đội thi nào được phân bổ
              </Typography>
            )}
          </Box>

          {caseItem.deadline && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Deadline nộp bài
              </Typography>
              <Typography variant="body1">
                {new Date(caseItem.deadline).toLocaleString('vi-VN')}
              </Typography>
            </Box>
          )}

          {caseItem.created_at && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Ngày tạo
              </Typography>
              <Typography variant="body2">
                {new Date(caseItem.created_at).toLocaleString('vi-VN')}
              </Typography>
            </Box>
          )}
        </Box>
        )}

        {/* Tab 2: Vòng thi (chỉ Secretary/Support) */}
        {tabValue === 1 && canViewAllCases && (
          <CaseRoundsTab caseId={caseItem.id} caseItem={caseItem} />
        )}

        {/* Tab 3: Tài liệu cuộc thi (chỉ Secretary/Support) */}
        {tabValue === 2 && canViewAllCases && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Tài liệu cuộc thi</Typography>
              <Button
                variant="contained"
                startIcon={<Upload />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload Tài liệu
              </Button>
            </Box>
            
            {isLoadingDocs ? (
              <CircularProgress />
            ) : caseDocuments.length === 0 ? (
              <Alert severity="info">Chưa có tài liệu nào</Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {caseDocuments.map((doc) => (
                  <Box
                    key={doc.id}
                    sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Description />
                      <Box>
                        <Typography variant="body1">{doc.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {doc.document_type} • {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => handleDownload(doc.id, doc.file_name)}>
                        <Download />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(doc.id)} color="error">
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* RAG chỉ dùng cho AI Assistive trong ScoringPage, không cần tab riêng ở đây */}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
      </DialogActions>

      {/* Assign Teams Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Phân bổ đội thi cho cuộc thi</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Chọn đội thi *</InputLabel>
              <Select
                multiple
                value={selectedTeamIds}
                onChange={(e) => setSelectedTeamIds(e.target.value as string[])}
                label="Chọn đội thi *"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((teamId) => {
                      const team = teams.find(t => t.id === teamId)
                      return team ? (
                        <Chip key={teamId} label={`${team.name} - ${team.university}`} size="small" />
                      ) : null
                    })}
                  </Box>
                )}
              >
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name} - {team.university}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Deadline nộp bài"
              type="datetime-local"
              value={assignDeadline}
              onChange={(e) => setAssignDeadline(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Alert severity="info">
              Bạn có thể chọn nhiều đội thi để phân bổ cho cuộc thi này. Các đội thi đã được phân bổ trước đó sẽ được giữ lại.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Hủy</Button>
          <Button
            onClick={async () => {
              setAssignErrorMessage(null)
              setAssignSuccessMessage(null)
              
              if (selectedTeamIds.length === 0) {
                setAssignErrorMessage('Vui lòng chọn ít nhất một đội thi')
                return
              }
              try {
                await assignCase({
                  caseId: caseItem.id,
                  assignment: {
                    team_ids: selectedTeamIds,
                    deadline: assignDeadline ? new Date(assignDeadline).toISOString() : undefined,
                  },
                }).unwrap()
                setAssignDialogOpen(false)
                await refetchCase()
                // Refetch danh sách cases bên ngoài
                if (onCaseUpdated) {
                  onCaseUpdated()
                }
                setAssignSuccessMessage('Phân bổ đội thi thành công!')
                // Auto close success message after 3 seconds
                setTimeout(() => setAssignSuccessMessage(null), 3000)
              } catch (error: any) {
                setAssignErrorMessage(`Phân bổ thất bại: ${error.data?.detail || error.message}`)
              }
            }}
            variant="contained"
            disabled={isAssigning}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
            }}
          >
            {isAssigning ? <CircularProgress size={20} /> : 'Phân bổ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Tài liệu cuộc thi</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Tiêu đề *"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Mô tả"
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Loại tài liệu *</InputLabel>
              <Select
                value={uploadForm.document_type}
                label="Loại tài liệu *"
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, document_type: e.target.value as DocumentType })
                }
              >
                <MenuItem value={DocumentType.CASE_DOCUMENT}>Tài liệu cuộc thi</MenuItem>
                <MenuItem value={DocumentType.REFERENCE}>Tài liệu tham khảo</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}>
              Chọn File
              <input type="file" hidden onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setUploadForm({ ...uploadForm, file: e.target.files[0] })
                }
              }} />
            </Button>
            {uploadForm.file && (
              <Typography variant="body2">
                Đã chọn: {uploadForm.file.name} ({(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleUpload} variant="contained" disabled={isUploading}>
            {isUploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

// RAG Search Tab Component
function RAGSearchTab({ 
  caseItem, 
  teams, 
  canScoreCases 
}: { 
  caseItem: Case
  teams: any[]
  canScoreCases: boolean 
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [teamId, setTeamId] = useState('')
  const [ragTab, setRagTab] = useState(0)
  const [results, setResults] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSearch = async (context: 'case' | 'team' | 'scoring') => {
    if (!searchQuery.trim()) {
      setError('Vui lòng nhập từ khóa tìm kiếm')
      return
    }

    setIsSearching(true)
    setError(null)

    try {
      let response: any

      if (context === 'case') {
        response = await fetch(`${API_BASE_URL}/documents/search/case-documents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            query: searchQuery,
            case_id: caseItem.id,
            limit: 10,
            similarity_threshold: 0.7,
          }),
        })
      } else if (context === 'team') {
        response = await fetch(`${API_BASE_URL}/documents/search/team-submissions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            query: searchQuery,
            case_id: caseItem.id,
            team_id: teamId || undefined,
            limit: 10,
            similarity_threshold: 0.7,
          }),
        })
      } else {
        if (!teamId.trim()) {
          setError('Vui lòng chọn đội thi để chấm điểm')
          setIsSearching(false)
          return
        }
        response = await fetch(`${API_BASE_URL}/documents/search/scoring`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            query: searchQuery,
            case_id: caseItem.id,
            team_id: teamId,
            limit: 20,
            similarity_threshold: 0.7,
            case_weight: 0.5,
            team_weight: 0.5,
          }),
        })
      }

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Search failed')
      }

      const data = await response.json()
      setResults(data)
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra khi tìm kiếm')
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      case_document: 'Tài liệu cuộc thi',
      reference: 'Tài liệu tham khảo',
      memorial: 'Hồ sơ khởi kiện',
      evidence: 'Chứng cứ',
      transcript: 'Transcript',
      rubric: 'Rubric',
    }
    return labels[type] || type
  }

  const assignedTeamIds = caseItem.assigned_team_ids || []

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        RAG Search - Tìm kiếm Tài liệu
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Từ khóa tìm kiếm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Ví dụ: Yêu cầu của nguyên đơn, Phân tích pháp lý..."
            multiline
            rows={2}
          />
          {ragTab === 1 && (
            <FormControl fullWidth>
              <InputLabel>Đội thi (tùy chọn)</InputLabel>
              <Select
                value={teamId}
                label="Đội thi (tùy chọn)"
                onChange={(e) => setTeamId(e.target.value)}
              >
                <MenuItem value="">Tất cả các đội</MenuItem>
                {assignedTeamIds.map((tid: string) => {
                  const team = teams.find((t) => t.id === tid)
                  return team ? (
                    <MenuItem key={tid} value={tid}>
                      {team.name} - {team.university}
                    </MenuItem>
                  ) : null
                })}
              </Select>
            </FormControl>
          )}
          {ragTab === 2 && (
            <FormControl fullWidth required>
              <InputLabel>Đội thi *</InputLabel>
              <Select
                value={teamId}
                label="Đội thi *"
                onChange={(e) => setTeamId(e.target.value)}
                required
              >
                <MenuItem value="">Chọn đội thi</MenuItem>
                {assignedTeamIds.map((tid: string) => {
                  const team = teams.find((t) => t.id === tid)
                  return team ? (
                    <MenuItem key={tid} value={tid}>
                      {team.name} - {team.university}
                    </MenuItem>
                  ) : null
                })}
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={() => {
              if (ragTab === 0) handleSearch('case')
              else if (ragTab === 1) handleSearch('team')
              else handleSearch('scoring')
            }}
            disabled={isSearching || (ragTab === 2 && !teamId)}
          >
            {isSearching ? <CircularProgress size={20} /> : 'Tìm kiếm'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs value={ragTab} onChange={(_, v) => setRagTab(v)}>
          <Tab label="Tài liệu cuộc thi (RAG 1)" />
          <Tab label="Tài liệu đội thi (RAG 2)" />
          {canScoreCases && <Tab label="Chấm điểm (RAG 3)" />}
        </Tabs>

        <Box sx={{ p: 2 }}>
          {ragTab === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tìm kiếm trong tài liệu cuộc thi và tài liệu tham khảo (CASE_DOCUMENT, REFERENCE)
              </Typography>
              {isSearching && <LinearProgress sx={{ mb: 2 }} />}
              <RAGSearchResults results={results} getDocumentTypeLabel={getDocumentTypeLabel} />
            </Box>
          )}

          {ragTab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tìm kiếm trong memorial, evidence, transcript từ các team (MEMORIAL, EVIDENCE, TRANSCRIPT)
              </Typography>
              {isSearching && <LinearProgress sx={{ mb: 2 }} />}
              <RAGSearchResults results={results} getDocumentTypeLabel={getDocumentTypeLabel} />
            </Box>
          )}

          {ragTab === 2 && canScoreCases && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Kết hợp tìm kiếm từ cả tài liệu cuộc thi và tài liệu đội thi để hỗ trợ chấm điểm
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Yêu cầu quyền SCORE_CASES (Judge/Secretary only)
              </Alert>
              {isSearching && <LinearProgress sx={{ mb: 2 }} />}
              <RAGSearchResults results={results} getDocumentTypeLabel={getDocumentTypeLabel} />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

// RAG Search Results Component
function RAGSearchResults({
  results,
  getDocumentTypeLabel,
}: {
  results: any[]
  getDocumentTypeLabel: (type: string) => string
}) {
  if (results.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Chưa có kết quả tìm kiếm. Hãy nhập từ khóa và nhấn "Tìm kiếm"
        </Typography>
      </Box>
    )
  }

  return (
    <Grid container spacing={2}>
      {results.map((result, index) => (
        <Grid item xs={12} key={result.chunk_id || index}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" component="h3">
                    {result.document_title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                      label={getDocumentTypeLabel(result.metadata?.document_type || '')}
                      size="small"
                      color="primary"
                    />
                    <Chip
                      label={`Similarity: ${(result.similarity_score * 100).toFixed(1)}%`}
                      size="small"
                      color="success"
                    />
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {result.content}
              </Typography>
              {result.metadata && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {result.metadata.case_id && (
                    <Chip label={`Case: ${result.metadata.case_id}`} size="small" variant="outlined" />
                  )}
                  {result.metadata.team_id && (
                    <Chip label={`Team: ${result.metadata.team_id}`} size="small" variant="outlined" />
                  )}
                </Box>
              )}
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<Description />}>
                Xem tài liệu
              </Button>
              <Button size="small" startIcon={<Download />}>
                Download
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}

// Case Rounds Tab Component
function CaseRoundsTab({ caseId, caseItem }: { caseId: string; caseItem: Case }) {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: rounds = [], isLoading, refetch } = useGetCaseRoundsQuery(caseId)
  const { data: currentCase, refetch: refetchCase } = useGetCaseQuery(caseId)
  const [updateRound, { isLoading: isUpdating }] = useUpdateRoundMutation()
  const [moveToNextRound, { isLoading: isMoving }] = useMoveToNextRoundMutation()
  const [moveToRound, { isLoading: isMovingToRound }] = useMoveToRoundMutation()

  const [openDialog, setOpenDialog] = useState(false)
  const [editingRound, setEditingRound] = useState<Round | null>(null)
  const [openRoundSelectDialog, setOpenRoundSelectDialog] = useState(false)
  const [movingToRoundId, setMovingToRoundId] = useState<string | null>(null) // Track which round is being moved to
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const canManageRounds = !!(currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES'))
  
  // Sắp xếp rounds theo trình tự: TRAINING -> MEMORIAL -> ONLINE_HEARING -> FINAL
  const roundOrder = [RoundType.TRAINING, RoundType.MEMORIAL, RoundType.ONLINE_HEARING, RoundType.FINAL]
  const sortedRounds = rounds.length > 0 
    ? [...rounds].sort((a, b) => roundOrder.indexOf(a.round_type) - roundOrder.indexOf(b.round_type))
    : []
  
  // Tìm vòng hiện tại
  const currentRoundId = currentCase?.current_round_id || caseItem.current_round_id
  const currentRoundIndex = sortedRounds.findIndex(r => r.id === currentRoundId)
  const canMoveToNext = currentRoundIndex >= 0 && currentRoundIndex < sortedRounds.length - 1
  const canMoveToPrevious = currentRoundIndex > 0

  const ROUND_TYPE_LABELS: Record<RoundType, string> = {
    [RoundType.TRAINING]: 'Vòng đào tạo',
    [RoundType.MEMORIAL]: 'Vòng memorial (chấm viết)',
    [RoundType.ONLINE_HEARING]: 'Vòng tranh tụng online',
    [RoundType.FINAL]: 'Chung kết trực tiếp',
  }

  const handleOpenDialog = (round: Round) => {
    setEditingRound(round)
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingRound(null)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleUpdateRound = async (roundId: string, updates: { start_date?: string; end_date?: string; status?: RoundStatus; meeting_link?: string }) => {
    try {
      await updateRound({ 
        id: roundId, 
        start_date: updates.start_date,
        end_date: updates.end_date,
        status: updates.status,
        meeting_link: updates.meeting_link,
      }).unwrap()
      setSuccessMessage('Cập nhật vòng thi thành công!')
      refetch()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra')
    }
  }

  const handleMoveToRound = async (roundId: string) => {
    setMovingToRoundId(roundId)
    setErrorMessage(null)
    try {
      await moveToRound({ caseId, roundId }).unwrap()
      setSuccessMessage('Đã chuyển vòng thành công!')
      setOpenRoundSelectDialog(false)
      setMovingToRoundId(null)
      refetch()
      refetchCase()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra khi chuyển vòng')
      setMovingToRoundId(null)
    }
  }

  const handleMoveToPreviousRound = async () => {
    if (!canMoveToPrevious || currentRoundIndex <= 0 || isMovingToRound) return
    
    const previousRound = sortedRounds[currentRoundIndex - 1]
    await handleMoveToRound(previousRound.id)
  }

  const handleMoveToNextRound = async () => {
    if (!canMoveToNext || isMoving) return
    
    setErrorMessage(null)
    try {
      await moveToNextRound(caseId).unwrap()
      setSuccessMessage('Đã chuyển sang vòng tiếp theo!')
      refetch()
      refetchCase()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra khi chuyển vòng')
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Quản lý Vòng thi</Typography>
        {canManageRounds && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canMoveToPrevious && (
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={handleMoveToPreviousRound}
                disabled={isMovingToRound}
                color="secondary"
              >
                {isMovingToRound ? <CircularProgress size={20} /> : 'Về vòng trước'}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={isMovingToRound ? <CircularProgress size={16} /> : <SwapHoriz />}
              onClick={() => setOpenRoundSelectDialog(true)}
              disabled={isMovingToRound || isMoving}
            >
              Chọn vòng
            </Button>
            {canMoveToNext && (
              <Button
                variant="contained"
                startIcon={<ArrowForward />}
                onClick={handleMoveToNextRound}
                disabled={isMoving}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  },
                }}
              >
                {isMoving ? <CircularProgress size={20} /> : 'Vòng tiếp theo'}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : sortedRounds.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Timeline sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Chưa có vòng thi nào
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Các vòng thi sẽ được tạo tự động khi tạo cuộc thi
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {sortedRounds.map((round, index) => {
            const isCurrentRound = round.id === currentRoundId
            const isPastRound = currentRoundIndex >= 0 && index < currentRoundIndex
            const isFutureRound = currentRoundIndex >= 0 && index > currentRoundIndex
            
            return (
              <Grid item xs={12} md={6} key={round.id}>
                <Card 
                  elevation={isCurrentRound ? 4 : 2} 
                  sx={{ 
                    height: '100%',
                    border: isCurrentRound ? 2 : 0,
                    borderColor: isCurrentRound ? 'primary.main' : 'transparent',
                    backgroundColor: isCurrentRound ? 'action.selected' : 'background.paper',
                    position: 'relative',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Typography variant="h6" fontWeight={600}>
                          {round.name}
                        </Typography>
                        {isCurrentRound && (
                          <Chip 
                            label="Vòng hiện tại" 
                            color="primary" 
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        )}
                        {isPastRound && (
                          <Chip 
                            label="Đã hoàn thành" 
                            color="default" 
                            size="small"
                          />
                        )}
                        {isFutureRound && (
                          <Chip 
                            label="Sắp tới" 
                            color="info" 
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Loại:
                      </Typography>
                      <Chip label={ROUND_TYPE_LABELS[round.round_type]} size="small" variant="outlined" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="body2">
                        {new Date(round.start_date).toLocaleDateString('vi-VN')} -{' '}
                        {new Date(round.end_date).toLocaleDateString('vi-VN')}
                      </Typography>
                    </Box>
                  </Box>

                  {round.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {round.description.length > 100 ? `${round.description.substring(0, 100)}...` : round.description}
                    </Typography>
                  )}

                  {/* Courtroom Info for ONLINE_HEARING rounds */}
                  {round.round_type === RoundType.ONLINE_HEARING && (
                    <CourtroomCard roundId={round.id} canManageRounds={!!canManageRounds} />
                  )}

                    {canManageRounds && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDialog(round)} 
                          color="primary"
                          title="Chỉnh sửa thời gian và trạng thái"
                        >
                          <Edit />
                        </IconButton>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      )}

      {/* Edit Round Dialog - Chỉ cho phép chỉnh sửa thời gian và trạng thái */}
      {editingRound && (
        <EditRoundDialog
          open={openDialog}
          onClose={handleCloseDialog}
          round={editingRound}
          onUpdate={handleUpdateRound}
          isUpdating={isUpdating}
        />
      )}

      {/* Round Selection Dialog */}
      <Dialog
        open={openRoundSelectDialog}
        onClose={() => !isMovingToRound && setOpenRoundSelectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <Backdrop
          open={isMovingToRound}
          sx={{
            position: 'absolute',
            zIndex: (theme: any) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={60} sx={{ color: 'white' }} />
            <Typography variant="h6" sx={{ color: 'white' }}>
              Đang chuyển vòng...
            </Typography>
          </Box>
        </Backdrop>
        <DialogTitle>Chọn vòng thi để chuyển đến</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Alert severity="info">
              Chọn vòng thi bạn muốn chuyển đến. Bạn có thể chuyển về vòng trước hoặc sang vòng sau bất kỳ.
            </Alert>
            {sortedRounds.map((round, index) => {
              const isCurrentRound = round.id === currentRoundId
              const isPastRound = currentRoundIndex >= 0 && index < currentRoundIndex
              const isFutureRound = currentRoundIndex >= 0 && index > currentRoundIndex
              const isMovingToThisRound = movingToRoundId === round.id
              
              return (
                <Card
                  key={round.id}
                  sx={{
                    cursor: isCurrentRound || isMovingToRound ? 'default' : 'pointer',
                    border: isCurrentRound ? 2 : 1,
                    borderColor: isCurrentRound ? 'primary.main' : isMovingToThisRound ? 'primary.main' : 'divider',
                    backgroundColor: isCurrentRound ? 'action.selected' : isMovingToThisRound ? 'action.hover' : 'background.paper',
                    opacity: isMovingToRound && !isMovingToThisRound ? 0.5 : 1,
                    position: 'relative',
                    '&:hover': isCurrentRound || isMovingToRound ? {} : {
                      backgroundColor: 'action.hover',
                      borderColor: 'primary.main',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                  onClick={() => !isCurrentRound && !isMovingToRound && handleMoveToRound(round.id)}
                >
                  {isMovingToThisRound && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={40} />
                    </Box>
                  )}
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6">
                            {ROUND_TYPE_LABELS[round.round_type]}
                          </Typography>
                          {isMovingToThisRound && (
                            <CircularProgress size={16} />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {round.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          {isCurrentRound && (
                            <Chip label="Vòng hiện tại" size="small" color="primary" />
                          )}
                          {isPastRound && (
                            <Chip label="Đã hoàn thành" size="small" color="default" />
                          )}
                          {isFutureRound && (
                            <Chip label="Sắp tới" size="small" color="info" />
                          )}
                        </Box>
                      </Box>
                      {isCurrentRound && (
                        <Chip label="Đang chọn" color="primary" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenRoundSelectDialog(false)}
            disabled={isMovingToRound}
          >
            Hủy
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Edit Round Dialog Component - Chỉ cho phép chỉnh sửa thời gian và trạng thái
function EditRoundDialog({
  open,
  onClose,
  round,
  onUpdate,
  isUpdating,
}: {
  open: boolean
  onClose: () => void
  round: Round
  onUpdate: (roundId: string, updates: { start_date?: string; end_date?: string; status?: RoundStatus; meeting_link?: string }) => Promise<void>
  isUpdating: boolean
}) {
  const [startDate, setStartDate] = useState(
    round.start_date ? new Date(round.start_date).toISOString().slice(0, 16) : ''
  )
  const [endDate, setEndDate] = useState(
    round.end_date ? new Date(round.end_date).toISOString().slice(0, 16) : ''
  )
  const [status, setStatus] = useState<RoundStatus>(round.status)
  const [meetingLink, setMeetingLink] = useState<string>(round.meeting_link || '')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const STATUS_LABELS: Record<RoundStatus, string> = {
    [RoundStatus.UPCOMING]: 'Sắp diễn ra',
    [RoundStatus.ACTIVE]: 'Đang diễn ra',
    [RoundStatus.COMPLETED]: 'Đã hoàn thành',
    [RoundStatus.CANCELLED]: 'Đã hủy',
  }

  const handleSubmit = async () => {
    setErrorMessage(null)

    if (!startDate || !endDate) {
      setErrorMessage('Ngày bắt đầu và ngày kết thúc là bắt buộc')
      return
    }

    if (new Date(startDate) >= new Date(endDate)) {
      setErrorMessage('Ngày kết thúc phải sau ngày bắt đầu')
      return
    }

    try {
      await onUpdate(round.id, {
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString(),
        status,
        meeting_link: meetingLink || undefined,
      })
      onClose()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Chỉnh sửa vòng thi: {round.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
          <Alert severity="info">
            Chỉ có thể chỉnh sửa thời gian, trạng thái và Google Meet link của vòng thi. Tên và loại vòng thi không thể thay đổi.
          </Alert>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Ngày bắt đầu"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Ngày kết thúc"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          </Box>
          <FormControl fullWidth>
            <InputLabel>Trạng thái</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as RoundStatus)}
              label="Trạng thái"
            >
              <MenuItem value={RoundStatus.UPCOMING}>{STATUS_LABELS[RoundStatus.UPCOMING]}</MenuItem>
              <MenuItem value={RoundStatus.ACTIVE}>{STATUS_LABELS[RoundStatus.ACTIVE]}</MenuItem>
              <MenuItem value={RoundStatus.COMPLETED}>{STATUS_LABELS[RoundStatus.COMPLETED]}</MenuItem>
              <MenuItem value={RoundStatus.CANCELLED}>{STATUS_LABELS[RoundStatus.CANCELLED]}</MenuItem>
            </Select>
          </FormControl>
          {round.round_type === RoundType.ONLINE_HEARING && (
            <TextField
              label="Google Meet Link"
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
              placeholder="https://meet.google.com/xxx-xxxx-xxx"
              fullWidth
              helperText="Link Google Meet sẽ được tự động tạo khi chuyển sang vòng này. Bạn có thể chỉnh sửa nếu cần."
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Hủy</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isUpdating}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
            },
          }}
        >
          {isUpdating ? <CircularProgress size={20} /> : 'Cập nhật'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Courtroom Card Component for ONLINE_HEARING rounds
function CourtroomCard({ roundId, canManageRounds }: { roundId: string; canManageRounds: boolean }) {
  const { data: courtroom, isLoading: isLoadingCourtroom, refetch: refetchCourtroom } = useGetCourtroomByRoundQuery(roundId, {
    skip: false, // Always fetch
  })
  const [joinCourtroom, { isLoading: isJoining }] = useJoinCourtroomMutation()
  const [endCourtroom, { isLoading: isEnding }] = useEndCourtroomMutation()

  const handleJoinCourtroom = async () => {
    if (!courtroom) return
    try {
      const result = await joinCourtroom(courtroom.id).unwrap()
      if (result.meeting_link) {
        window.open(result.meeting_link, '_blank')
      }
      refetchCourtroom()
    } catch (error: any) {
      console.error('Failed to join courtroom:', error)
    }
  }

  const handleEndCourtroom = async () => {
    if (!courtroom) return
    if (!confirm('Bạn có chắc muốn kết thúc courtroom này?')) return
    try {
      await endCourtroom(courtroom.id).unwrap()
      refetchCourtroom()
    } catch (error: any) {
      console.error('Failed to end courtroom:', error)
    }
  }

  if (isLoadingCourtroom) {
    return <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1, mb: 2 }} />
  }

  if (!courtroom) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Courtroom chưa được tạo. Courtroom sẽ được tạo tự động khi chuyển sang vòng này.
      </Alert>
    )
  }

  const STATUS_LABELS: Record<CourtroomStatus, string> = {
    [CourtroomStatus.SCHEDULED]: 'Đã lên lịch',
    [CourtroomStatus.ACTIVE]: 'Đang diễn ra',
    [CourtroomStatus.COMPLETED]: 'Đã kết thúc',
    [CourtroomStatus.CANCELLED]: 'Đã hủy',
  }

  const STATUS_COLORS: Record<CourtroomStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    [CourtroomStatus.SCHEDULED]: 'info',
    [CourtroomStatus.ACTIVE]: 'success',
    [CourtroomStatus.COMPLETED]: 'default',
    [CourtroomStatus.CANCELLED]: 'error',
  }

  return (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: 'action.hover' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VideoCall fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            Virtual Courtroom
          </Typography>
        </Box>
        <Chip
          label={STATUS_LABELS[courtroom.status]}
          color={STATUS_COLORS[courtroom.status]}
          size="small"
        />
      </Box>

      {courtroom.meeting_link && (
        <Box sx={{ mb: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<OpenInNew />}
            onClick={handleJoinCourtroom}
            disabled={isJoining || courtroom.status === CourtroomStatus.COMPLETED}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
            }}
          >
            {isJoining ? <CircularProgress size={16} /> : 'Tham gia Google Meet'}
          </Button>
        </Box>
      )}

      {courtroom.participants && courtroom.participants.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <People fontSize="small" color="action" />
          <Typography variant="caption" color="text.secondary">
            {courtroom.participants.length} người tham gia
          </Typography>
        </Box>
      )}

      {courtroom.recording_url && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            startIcon={<Download />}
            href={courtroom.recording_url}
            target="_blank"
            variant="outlined"
          >
            Tải Recording
          </Button>
        </Box>
      )}

      {canManageRounds && courtroom.status === CourtroomStatus.ACTIVE && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={handleEndCourtroom}
            disabled={isEnding}
          >
            {isEnding ? <CircularProgress size={16} /> : 'Kết thúc Courtroom'}
          </Button>
        </Box>
      )}
    </Paper>
  )
}
